/**
 * Headline volume over the session, and the window both session charts share.
 *
 * ## What this is for
 *
 * The Fundamentals screen draws risk appetite as a ribbon and headline volume
 * as a histogram directly beneath it, on ONE x-axis. That shared axis is the
 * whole point: a spike in the histogram sitting under a bend in the ribbon says
 * "the news moved the market" in a way two separately-scaled charts cannot.
 *
 * Which means the axis cannot be defined twice. `SessionWindow` below is the
 * single definition, and both series are bucketed against it. Two charts that
 * each decide their own start time drift apart by however long the two requests
 * were separated by, and the alignment silently becomes decorative.
 *
 * Pure, per `packages/core` convention — no clock, no I/O. `now` is passed in.
 */
import type { ReleaseImportance } from './types.js';

/**
 * The time axis both session charts are drawn against.
 *
 * `endMs` is exclusive. A bucket owns `[start, start + bucketMs)`, so an entry
 * landing exactly on a boundary belongs to the later bucket and is counted
 * once — not zero times, and not twice.
 */
export interface SessionWindow {
  readonly startMs: number;
  readonly endMs: number;
  readonly bucketMs: number;
  readonly buckets: number;
}

/**
 * Build the shared axis.
 *
 * `buckets` is clamped to at least 1: a caller asking for zero buckets has a
 * bug, and returning an empty axis turns it into an invisible one — the chart
 * renders nothing and nobody can tell whether the news is quiet or the code is
 * broken.
 */
export function sessionWindow(now: Date, spanMs: number, buckets: number): SessionWindow {
  const n = Math.max(1, Math.floor(buckets));
  const span = Math.max(1, spanMs);
  const endMs = now.getTime();
  return {
    startMs: endMs - span,
    endMs,
    // Integer division would drop the remainder and leave the last bucket
    // covering a different width than the rest, which tilts the histogram.
    bucketMs: span / n,
    buckets: n,
  };
}

/** Anything with a publication time and a class. Structural on purpose. */
export interface PulseInput {
  readonly publishedAt: string;
  readonly importance: ReleaseImportance;
}

export interface PulseBucket {
  /** Left edge, ms. The right edge is `startMs + window.bucketMs`. */
  readonly startMs: number;
  readonly count: number;
  /**
   * The loudest class in the bucket, or `null` when the bucket is empty.
   *
   * Carried so the bar can be coloured by what landed rather than by how much
   * did. Ten routine vendor stories are not a CPI print, and a histogram that
   * only knows height says they are.
   */
  readonly peak: ReleaseImportance | null;
}

const RANK: Record<ReleaseImportance, number> = { normal: 0, high: 1, critical: 2 };

/**
 * Count entries into the window's buckets.
 *
 * Entries outside the window are DROPPED, not clamped to the nearest edge.
 * Clamping is the tempting one-liner and it lies loudly: every story older than
 * the session piles into bucket zero, and a quiet morning after a busy week
 * renders as a wall at the left edge. An entry that is not in the window is not
 * in the picture.
 *
 * Unparseable timestamps are dropped for the same reason. `Date.parse` returns
 * NaN, NaN fails every comparison, and the arithmetic below would land it at
 * index NaN — so it is excluded explicitly rather than by accident.
 */
export function newsPulse(
  entries: readonly PulseInput[],
  window: SessionWindow,
): readonly PulseBucket[] {
  const counts = new Array<number>(window.buckets).fill(0);
  const peaks = new Array<ReleaseImportance | null>(window.buckets).fill(null);

  for (const e of entries) {
    const t = Date.parse(e.publishedAt);
    /* Explicit, though the `undefined` check below would also absorb a NaN
       index (`counts[NaN]` is undefined, so the write is skipped and the array
       is untouched). Mutation testing confirmed removing this line changes no
       observable behaviour today — it is kept as the stated intent, so that a
       future cleanup of the `noUncheckedIndexedAccess` workaround cannot
       silently take the NaN handling with it. */
    if (!Number.isFinite(t)) continue;
    if (t < window.startMs || t >= window.endMs) continue;

    // Floor of the offset in bucket widths. `endMs` is excluded above, so this
    // cannot reach `buckets`; the clamp is belt-and-braces against a caller
    // passing a window whose fields disagree with each other.
    const i = Math.min(window.buckets - 1, Math.floor((t - window.startMs) / window.bucketMs));

    /* `noUncheckedIndexedAccess` is on, so an index read is `T | undefined`
       even where the index is provably in range. Read once into a local and
       branch on it rather than asserting non-null: the assertion would also
       silence a genuine out-of-range bug here, which is exactly the case this
       function's clamp exists to survive. */
    const count = counts[i];
    if (count === undefined) continue;
    counts[i] = count + 1;

    const cur = peaks[i];
    if (cur === undefined || cur === null || RANK[e.importance] > RANK[cur]) {
      peaks[i] = e.importance;
    }
  }

  return counts.map((count, i) => ({
    startMs: window.startMs + i * window.bucketMs,
    count,
    peak: peaks[i] ?? null,
  }));
}

/**
 * The busiest bucket's count, for scaling the histogram.
 *
 * Returns 0 for an empty session — and callers must treat 0 as "draw nothing",
 * not as a divisor. Normalising by a max of zero produces NaN heights, which
 * SVG renders as an invisible chart rather than an error.
 */
export function peakCount(buckets: readonly PulseBucket[]): number {
  let max = 0;
  for (const b of buckets) if (b.count > max) max = b.count;
  return max;
}
